import React, { useRef, useEffect, useState } from "react";
import { IonIcon, IonToast } from "@ionic/react";
import { chevronDownOutline, happyOutline, imageOutline } from "ionicons/icons";
import {
  BottomActionsBarProps,
  MOODS,
  AUDIENCES,
} from "../../types/socialJournal";
import { formatFileSize } from "../../utils/imageCompression";
import "./BottomActionsBar.css";

const BottomActionsBar: React.FC<BottomActionsBarProps> = ({
  selectedAudience,
  selectedMood,
  onAudienceChange,
  onMoodChange,
  onImageSelect,
  showAudiencePopover,
  showMoodSelector,
  onToggleAudiencePopover,
  onToggleMoodSelector,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const moodDropdownRef = useRef<HTMLDivElement>(null);
  const moodContainerRef = useRef<HTMLDivElement>(null);
  const moodRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [showSizeWarning, setShowSizeWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [hoveredMood, setHoveredMood] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedMoodAnimation, setSelectedMoodAnimation] = useState<
    string | null
  >(null);

  const selectedMoodObj = selectedMood
    ? MOODS.find((m) => m.label.toLowerCase() === selectedMood.toLowerCase())
    : undefined;

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);

      // Check for large files and show info message
      const largeFiles = fileArray.filter((file) => file.size > 1024 * 1024); // Files > 1MB
      if (largeFiles.length > 0) {
        const sizes = largeFiles
          .map((f) => `${f.name}: ${formatFileSize(f.size)}`)
          .join(", ");
        setWarningMessage(
          `Images will be compressed for optimal upload. Original sizes: ${sizes}`
        );
        setShowSizeWarning(true);
      }

      onImageSelect(fileArray);
    }
    // Reset the input value to allow re-selecting the same files
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAudienceSelect = (audience: string) => {
    onAudienceChange(audience);
  };

  const handleMoodSelect = (moodLabel: string) => {
    setSelectedMoodAnimation(moodLabel);
    onMoodChange(moodLabel.toLowerCase());
    setTimeout(() => {
      onToggleMoodSelector();
      setHoveredMood(null);
      setSelectedMoodAnimation(null);
    }, 150);
  };

  // Handle touch/mouse move for sliding gesture
  const handlePointerMove = (e: React.PointerEvent | TouchEvent) => {
    if (!isDragging || !moodContainerRef.current) return;

    const clientX =
      "touches" in e ? e.touches[0].clientX : (e as React.PointerEvent).clientX;

    // Find which mood is being hovered
    Array.from(moodRefs.current.entries()).forEach(([moodLabel, element]) => {
      const rect = element.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        if (hoveredMood !== moodLabel) {
          setHoveredMood(moodLabel);
        }
        return;
      }
    });
  };

  const handlePointerDown = () => {
    setIsDragging(true);
  };

  const handlePointerUp = () => {
    if (isDragging && hoveredMood) {
      handleMoodSelect(hoveredMood);
    }
    setIsDragging(false);
  };

  // Add touch event listeners for mood selector
  useEffect(() => {
    const container = moodContainerRef.current;
    if (!container || !showMoodSelector) return;

    const handleTouchMove = (e: TouchEvent) => handlePointerMove(e);
    const handleTouchEnd = () => handlePointerUp();

    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, hoveredMood, showMoodSelector]);

  // Reset states when closing
  useEffect(() => {
    if (!showMoodSelector) {
      setHoveredMood(null);
      setIsDragging(false);
      setSelectedMoodAnimation(null);
    }
  }, [showMoodSelector]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMoodSelector &&
        moodDropdownRef.current &&
        !moodDropdownRef.current.contains(event.target as Node)
      ) {
        onToggleMoodSelector();
      }
    };

    if (showMoodSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMoodSelector, onToggleMoodSelector]);

  return (
    <>
      <IonToast
        isOpen={showSizeWarning}
        onDidDismiss={() => setShowSizeWarning(false)}
        message={warningMessage}
        duration={3000}
        position="top"
        color="primary"
      />
      <div className="bottom-actions-bar bg-white px-2  lg:px-6  pt-4 mt-1   pb-4">
        <div className="flex items-start justify-between">
          {/* Left Section: Audience */}
          <div className="flex items-center pl-3  gap-6">
            <div className="relative">
              <button
                onClick={onToggleAudiencePopover}
                className="flex items-center gap-2 pe-2 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                type="button"
              >
                <span className="text-lg text-blue-500">
                  {selectedAudience}
                </span>
                <IonIcon
                  icon={chevronDownOutline}
                  className="text-blue-500 w-5 h-5"
                />
              </button>

              {showAudiencePopover && (
                <div className="absolute bottom-full left-0 mb-1 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 min-w-[180px] z-50">
                  {AUDIENCES.map((audience) => (
                    <button
                      key={audience}
                      onClick={() => handleAudienceSelect(audience)}
                      className="w-full px-4 py-2 text-left text-base hover:bg-gray-50"
                      type="button"
                    >
                      {audience}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Section: Image + Mood */}
          <div className="flex items-center gap-4">
            {/* Image Button */}
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef && fileInputRef.current?.click()}
                className="flex items-center gap-2 py-2.5 px-4 rounded-xl"
                aria-label="Add image"
              >
                <IonIcon
                  icon={imageOutline}
                  className="text-blue-500 w-4 h-4"
                />
                <span className="">Add</span>
              </button>
            </div>

            {/* Mood Selector */}
            <div className="relative " ref={moodDropdownRef}>
              <button
                onClick={onToggleMoodSelector}
                className="flex items-center  hover:bg-gray-50 gap-2 rounded-xl transition-colors"
                type="button"
              >
                {selectedMoodObj?.icon ? (
                  (() => {
                    const MoodIcon = selectedMoodObj.icon;
                    return (
                      <MoodIcon
                        className="w-4 h-4"
                        aria-label={selectedMoodObj.label}
                      />
                    );
                  })()
                ) : (
                  <IonIcon
                    icon={happyOutline}
                    className="text-blue-500 w-4 h-4"
                  />
                )}
                <span
                  className="text-base font-semibold"
                  style={{ color: "#333" }}
                >
                  {selectedMoodObj?.label || "Feeling"}
                </span>
                <IonIcon
                  icon={chevronDownOutline}
                  className="w-5 h-5"
                  style={{ color: selectedMoodObj?.color || "#3B82F6" }}
                />
              </button>

              {showMoodSelector && (
                <div
                  ref={moodContainerRef}
                  className="mood-selector-container p-4 gap-2"
                  onPointerMove={handlePointerMove}
                  onPointerDown={handlePointerDown}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={() => setHoveredMood(null)}
                  onTouchStart={handlePointerDown}
                >
                  <div className="flex  gap-4">
                    {MOODS.map((mood) => (
                      <div
                        key={mood.label}
                        ref={(el) => {
                          if (el) moodRefs.current.set(mood.label, el);
                        }}
                        className={`mood-option-wrapper ${
                          hoveredMood === mood.label ? "hovered" : ""
                        } ${
                          selectedMoodAnimation === mood.label ? "selected" : ""
                        }`}
                      >
                        {hoveredMood === mood.label && (
                          <div
                            className="mood-label-tooltip text-base px-2 py-1 "
                            style={{
                              backgroundColor: mood.color,
                              color: mood.labelColor,
                              border: `1px solid ${mood.labelColor}`,
                            }}
                          >
                            {mood.label}
                          </div>
                        )}
                        <button
                          onClick={() =>
                            !isDragging && handleMoodSelect(mood.label)
                          }
                          className="mood-option-button w-12 h-12 rounded-[40px]"
                          style={{
                            backgroundColor: mood.color,
                          }}
                          type="button"
                        >
                          {mood.icon &&
                            (() => {
                              const MoodIcon = mood.icon;
                              return (
                                <MoodIcon
                                  className="w-7 h-7"
                                  aria-label={mood.label}
                                />
                              );
                            })()}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BottomActionsBar;
